import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  FlaskConical,
  Building2,
  Users,
  HelpCircle,
  MessageSquare,
  Calendar,
  AlertTriangle,
  Sparkles,
  Shield,
  Settings,
  ScrollText,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Search,
  Globe,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "@/api/firebase";
import { cn } from "@/lib/utils";
import type { Permission } from "@/lib/permissions";
import { useUIStore } from "@/stores/ui-store";
import { useAuth } from "@/auth/useAuth";
import { useOrgBranding } from "@/hooks/use-org-branding";

interface NavItem {
  icon: LucideIcon;
  label: string;
  href: string;
  badge?: number;
}

interface NavSection {
  label: string;
  requiresPermission?: Permission;
  requiresPlatformAdmin?: boolean;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    label: "OVERVIEW",
    items: [
      { icon: LayoutDashboard, label: "Dashboard", href: "/dashboard" },
      { icon: Calendar, label: "Calendar", href: "/calendar" },
      { icon: Globe, label: "Site Directory", href: "/site-directory" },
    ],
  },
  {
    label: "CLINICAL DATA",
    items: [
      { icon: FlaskConical, label: "Studies", href: "/studies" },
      { icon: Building2, label: "Sites", href: "/sites" },
      { icon: Users, label: "Subjects", href: "/subjects" },
      { icon: HelpCircle, label: "Queries", href: "/queries" },
    ],
  },
  {
    label: "TOOLS",
    items: [
      { icon: AlertTriangle, label: "Alerts", href: "/alerts" },
      { icon: MessageSquare, label: "AI Chat", href: "/chat" },
      { icon: Search, label: "Search", href: "/search" },
    ],
  },
  {
    label: "ADMIN",
    requiresPermission: "connections:manage" as Permission,
    items: [
      { icon: Shield, label: "Users & Roles", href: "/users" },
      { icon: Settings, label: "Settings", href: "/settings" },
      { icon: CreditCard, label: "Billing", href: "/billing" },
      { icon: ScrollText, label: "Audit Trail", href: "/audit" },
      { icon: Sparkles, label: "AI Skills", href: "/skills" },
    ],
  },
  {
    label: "MEDTECH-OS",
    requiresPlatformAdmin: true,
    items: [
      { icon: LayoutDashboard, label: "Internal Dashboard", href: "/internal/dashboard" },
    ],
  },
];

function SidebarContent({ collapsed, onNavClick }: { collapsed: boolean; onNavClick?: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { toggleSidebar } = useUIStore();
  const { can, displayName, role, isPlatformAdmin } = useAuth();
  const { data: branding } = useOrgBranding();

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login");
  };

  const isActive = (href: string) =>
    location.pathname === href || location.pathname.startsWith(href + "/");

  return (
    <>
      {/* Logo & collapse toggle */}
      <div className="flex h-14 items-center justify-between border-b border-sidebar-border px-4">
        {!collapsed && (
          branding?.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt="Organization logo"
              className="h-8 max-w-[120px] object-contain"
            />
          ) : (
            <span className="text-lg font-bold tracking-tight text-sidebar-active-foreground">
              {branding?.displayName ?? "MedTechOS"}
            </span>
          )
        )}
        {collapsed && branding?.logoUrl && (
          <img
            src={branding.logoUrl}
            alt="Organization logo"
            className="h-6 w-6 object-contain mx-auto"
          />
        )}
        <button
          onClick={toggleSidebar}
          className={cn(
            "hidden h-8 w-8 items-center justify-center rounded-md text-sidebar-muted hover:bg-sidebar-border hover:text-sidebar-foreground transition-colors md:flex",
            collapsed && "mx-auto",
          )}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-2 py-4">
        {navSections.map((section) => {
          if (section.requiresPermission && !can(section.requiresPermission)) {
            return null;
          }
          if (section.requiresPlatformAdmin && !isPlatformAdmin) {
            return null;
          }

          return (
              <div key={section.label} className="mb-6">
                {!collapsed && (
                  <span className="mb-2 block px-3 text-[11px] font-semibold uppercase tracking-wider text-sidebar-muted">
                    {section.label}
                  </span>
                )}

                <ul className="space-y-1">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.href);

                    return (
                      <li key={item.href}>
                        <Link
                          to={item.href}
                          onClick={onNavClick}
                          className={cn(
                            "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                            active
                              ? "bg-sidebar-active text-sidebar-active-foreground"
                              : "text-sidebar-foreground hover:bg-sidebar-border hover:text-sidebar-active-foreground",
                            collapsed && "justify-center px-0",
                          )}
                          title={collapsed ? item.label : undefined}
                        >
                          <Icon className="h-5 w-5 shrink-0" />
                          {!collapsed && (
                            <span className="truncate">{item.label}</span>
                          )}
                          {!collapsed && item.badge != null && (
                            <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-bold text-destructive-foreground">
                              {item.badge}
                            </span>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
          );
        })}
      </nav>

      {/* User menu */}
      <div className="border-t border-sidebar-border p-3">
        <div
          className={cn(
            "flex items-center gap-3",
            collapsed && "justify-center",
          )}
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sidebar-active text-sm font-bold text-sidebar-active-foreground">
            {displayName ? displayName.charAt(0).toUpperCase() : "U"}
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-sidebar-active-foreground">
                {displayName ?? "User"}
              </p>
              {role && (
                <span className="inline-block rounded bg-sidebar-border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-sidebar-muted">
                  {role.replace("_", " ")}
                </span>
              )}
            </div>
          )}
          <button
            onClick={handleLogout}
            className={cn(
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-sidebar-muted hover:bg-sidebar-border hover:text-sidebar-active-foreground transition-colors",
              collapsed && "mx-auto mt-2",
            )}
            title="Log out"
            aria-label="Log out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  );
}

export function Sidebar() {
  const { sidebarCollapsed, mobileMenuOpen, closeMobileMenu } = useUIStore();

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={cn(
          "hidden h-screen flex-col bg-sidebar text-sidebar-foreground transition-all duration-300 ease-in-out md:flex",
          sidebarCollapsed ? "w-16" : "w-64",
        )}
      >
        <SidebarContent collapsed={sidebarCollapsed} />
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={closeMobileMenu} />
          <aside className="relative flex h-full w-64 flex-col bg-sidebar text-sidebar-foreground">
            <SidebarContent collapsed={false} onNavClick={closeMobileMenu} />
          </aside>
        </div>
      )}
    </>
  );
}
